import { useMemo, useState } from 'react';
import * as Slider from '@radix-ui/react-slider';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { HomeFacetValue, HomeFacetsResponse } from '../api/client.js';
import {
  CheckboxRow,
  CollapsibleFilterRow,
  FilterModalShell,
  MultiSelectList,
  ValuePill,
  summarizeSelection,
  type SelectOption,
} from './filter-modal-kit.js';
import {
  HOME_CATEGORICAL_LABELS,
  HOME_HAS_OPTIONS,
  HOME_RANGE_SPEC_BY_KEY,
  homeValueLabel,
  isHomeRangeActive,
  summarizeHomeRange,
  type HomeCategoricalKey,
  type HomeCategoricals,
  type HomeRange,
  type HomeRangeKey,
  type HomeRangeSpec,
  type HomeRanges,
} from './homes-filters.js';

// Filter modal for the Homes screener. Same visual recipe as Triage's filter
// modal (filter-modal-kit: centred dialog, collapsible rows with a value pill,
// vertical checkbox lists, slider ranges), grouped into sections because the
// home has many more axes than a company. Option counts come from
// GET /homes/facets under the CURRENT filters, each facet with its own filter
// lifted — so a list shows what selecting a value would add.

type Section = {
  title: string;
  categoricals: readonly HomeCategoricalKey[];
  ranges: readonly HomeRangeKey[];
};

const SECTIONS: readonly Section[] = [
  {
    title: 'The home',
    categoricals: [
      'lifecycle',
      'country',
      'urbanicity',
      'setting',
      'careLevel',
      'cohort',
      'stay',
      'pinLevel',
    ],
    ranges: ['beds', 'buildingYear'],
  },
  {
    title: 'Operator and property',
    categoricals: ['operatorSector', 'tenure', 'landlordType'],
    ranges: ['operatorHomes'],
  },
  {
    title: 'Quality and money',
    categoricals: ['quality', 'altSignal', 'ratingFamily', 'publicFunding', 'payerPressure'],
    ranges: ['fee'],
  },
  {
    title: 'Catchment, workforce and hazard',
    categoricals: ['floodVerdict', 'heatVerdict'],
    ranges: ['incomePctl', 'growth65', 'growth80', 'placesPer1000Elderly', 'vacancyRate'],
  },
];

/** Values always offered even when the facet has no rows yet. */
const FIXED_OPTIONS: Partial<Record<HomeCategoricalKey, readonly string[]>> = {
  lifecycle: ['operating', 'development'],
  // Quality lists grades best → worst, then the signals for ungraded homes (always offered, in this order).
  quality: [
    'good',
    'requires_improvement',
    'poor',
    'signal_positive',
    'signal_mixed',
    'signal_neutral',
    'signal_negative',
  ],
  altSignal: ['positive', 'mixed', 'neutral', 'negative'],
};

/** Keys whose options keep the FIXED_OPTIONS order instead of sorting by count. */
const KEEP_ORDER: ReadonlySet<HomeCategoricalKey> = new Set(['quality', 'altSignal']);

export interface HomesFilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categoricals: HomeCategoricals;
  setCategorical: (key: HomeCategoricalKey, next: Set<string>) => void;
  ranges: HomeRanges;
  setRange: (key: HomeRangeKey, next: HomeRange) => void;
  has: ReadonlySet<string>;
  setHas: (next: Set<string>) => void;
  facets: HomeFacetsResponse | null;
  onClearAll: () => void;
}

export function HomesFilterModal({
  open,
  onOpenChange,
  categoricals,
  setCategorical,
  ranges,
  setRange,
  has,
  setHas,
  facets,
  onClearAll,
}: HomesFilterModalProps) {
  return (
    <FilterModalShell
      open={open}
      onOpenChange={onOpenChange}
      title="Filter homes"
      resetLabel="Reset"
      doneLabel="Done"
      onReset={onClearAll}
    >
      {SECTIONS.map((s) => (
        <div key={s.title} style={{ display: 'flex', flexDirection: 'column' }}>
          <SectionTitle>{s.title}</SectionTitle>
          {s.categoricals.map((k) => (
            <CategoricalRow
              key={k}
              k={k}
              selected={categoricals[k]}
              onChange={(next) => setCategorical(k, next)}
              facet={facets?.facets[k]}
            />
          ))}
          {s.ranges.map((k) => (
            <RangeSliderRow
              key={k}
              spec={HOME_RANGE_SPEC_BY_KEY[k]}
              value={ranges[k]}
              onChange={(next) => setRange(k, next)}
            />
          ))}
        </div>
      ))}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <SectionTitle>Has data</SectionTitle>
        <CollapsibleFilterRow
          label="Only homes with…"
          summary={summarizeSelection(
            has,
            (c) => HOME_HAS_OPTIONS.find((o) => o.column === c)?.label ?? c,
          )}
          active={has.size > 0}
        >
          {HOME_HAS_OPTIONS.map((o) => (
            <CheckboxRow
              key={o.column}
              checked={has.has(o.column)}
              label={o.label}
              onToggle={() => {
                const next = new Set(has);
                if (next.has(o.column)) next.delete(o.column);
                else next.add(o.column);
                setHas(next);
              }}
            />
          ))}
        </CollapsibleFilterRow>
      </div>
    </FilterModalShell>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 'var(--s-2) var(--s-2) 4px',
        fontSize: 'var(--t-meta)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontWeight: 700,
        color: 'var(--text-tertiary)',
      }}
    >
      {children}
    </div>
  );
}

function CategoricalRow({
  k,
  selected,
  onChange,
  facet,
}: {
  k: HomeCategoricalKey;
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  facet: HomeFacetValue[] | undefined;
}) {
  const options = useMemo<SelectOption[]>(() => {
    const counts = new Map<string, number>();
    // The facet's null bucket ("not recorded") is not filterable — an IN list
    // never matches NULL — so it is left out of the options.
    for (const f of facet ?? []) if (f.value !== null) counts.set(f.value, f.homes);
    const values = new Set<string>([...(FIXED_OPTIONS[k] ?? []), ...counts.keys(), ...selected]);
    const ordered = Array.from(values).sort((a, b) => {
      if (k === 'country') return homeValueLabel(k, a).localeCompare(homeValueLabel(k, b));
      if (KEEP_ORDER.has(k)) {
        const order = FIXED_OPTIONS[k] ?? [];
        return order.indexOf(a) - order.indexOf(b);
      }
      return (counts.get(b) ?? 0) - (counts.get(a) ?? 0);
    });
    return ordered.map((v) => ({
      value: v,
      label: `${homeValueLabel(k, v)} · ${(counts.get(v) ?? 0).toLocaleString('en-GB')}`,
    }));
  }, [facet, k, selected]);
  return (
    <CollapsibleFilterRow
      label={HOME_CATEGORICAL_LABELS[k]}
      summary={summarizeSelection(selected, (v) => homeValueLabel(k, v))}
      active={k === 'lifecycle' ? false : selected.size > 0}
    >
      <MultiSelectList options={options} selected={selected} onChange={onChange} />
    </CollapsibleFilterRow>
  );
}

/** Two-thumb slider; a thumb resting on its bound means "open" on that side. */
function RangeSliderRow({
  spec,
  value,
  onChange,
}: {
  spec: HomeRangeSpec;
  value: HomeRange;
  onChange: (next: HomeRange) => void;
}) {
  const active = isHomeRangeActive(value);
  const [open, setOpen] = useState(active);
  const lo = value[0] ?? spec.min;
  const hi = value[1] ?? spec.max;
  return (
    <div style={{ borderTop: '1px solid var(--hairline)', background: 'var(--surface)' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-2)',
          width: '100%',
          padding: '6px var(--s-2)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 'var(--t-body)',
          color: 'var(--text-primary)',
        }}
      >
        <span style={{ flex: 1 }}>{spec.label}</span>
        <ValuePill active={active}>{summarizeHomeRange(spec, value)}</ValuePill>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <div
          style={{
            padding: '4px var(--s-2) var(--s-2)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <Slider.Root
            value={[lo, hi]}
            min={spec.min}
            max={spec.max}
            step={spec.step}
            onValueChange={(vs) => {
              if (vs.length !== 2) return;
              const a = vs[0]!;
              const b = vs[1]!;
              onChange([a <= spec.min ? null : a, b >= spec.max ? null : b]);
            }}
            aria-label={spec.label}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              userSelect: 'none',
              touchAction: 'none',
              width: '100%',
              height: 20,
            }}
          >
            <Slider.Track
              style={{
                position: 'relative',
                flexGrow: 1,
                background: 'var(--hairline)',
                height: 3,
                borderRadius: 2,
              }}
            >
              <Slider.Range
                style={{ position: 'absolute', background: 'var(--accent)', height: '100%' }}
              />
            </Slider.Track>
            <Slider.Thumb style={THUMB} />
            <Slider.Thumb style={THUMB} />
          </Slider.Root>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--t-body)',
              color: 'var(--text-secondary)',
            }}
          >
            <span>{value[0] === null ? 'Any' : spec.format(value[0])}</span>
            <span>{value[1] === null ? 'Any' : spec.format(value[1])}</span>
          </div>
          {spec.hint && (
            <span style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)' }}>
              {spec.hint}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const THUMB: React.CSSProperties = {
  display: 'block',
  width: 12,
  height: 12,
  background: 'var(--surface)',
  border: '2px solid var(--accent)',
  borderRadius: 8,
  cursor: 'pointer',
};
